import { create } from "zustand";

export type ToastVariant = "success" | "error" | "info" | "warning" | "undo";

export interface ToastAction {
  label: string;
  onClick: () => void | Promise<void>;
}

export interface Toast {
  id: number;
  title: string;
  description?: string;
  variant: ToastVariant;
  duration?: number;
  action?: ToastAction;
}

interface ToastState {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id">) => number;
  dismiss: (id: number) => void;
  clear: () => void;
}

let toastId = 0;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],

  push: (toast) => {
    const id = ++toastId;
    const duration = toast.duration ?? (toast.action ? 6000 : 3500);
    set((state) => ({ toasts: [...state.toasts, { ...toast, id, duration }] }));

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
      }, duration);
    }

    return id;
  },

  dismiss: (id) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },

  clear: () => set({ toasts: [] }),
}));

/**
 * Convenient helper to show a toast, optionally with an "Undo" action,
 * from anywhere (no hooks needed).
 */
export function showToast(
  title: string,
  options?: Partial<Omit<Toast, "id" | "title">> & { onUndo?: () => void | Promise<void> }
): void {
  const { onUndo, ...rest } = options || {};
  useToastStore.getState().push({
    title,
    variant: rest.variant || (onUndo ? "undo" : "info"),
    description: rest.description,
    duration: rest.duration,
    action: onUndo ? { label: "Deshacer", onClick: onUndo } : rest.action,
  });
}
